
import { useState, useEffect, useMemo } from 'react';
import { storeService } from '../services/storeService';
import { Store } from '../types';
import { DEFAULT_STORE_ID } from '../constants';

/**
 * useStore: Lynqar ke Multi-Store architecture ka controller.
 * Ye hook handle karta hai ki app abhi kis business/store context mein hai.
 */
export const useStore = (storeSlug?: string) => {
  const [currentStore, setCurrentStore] = useState<Store | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const resolveStore = async () => {
      setIsLoading(true);
      
      let store: Store | null = null;

      // 1. Agar URL mein slug hai (e.g. /store/gadget-hub)
      if (storeSlug) {
        store = storeService.getStoreBySlug(storeSlug);
      } 
      
      // 2. Agar slug nahi mila, toh default store use karein (Initial Launch Phase)
      if (!store) {
        store = storeService.getStoreById(DEFAULT_STORE_ID);
      }

      setCurrentStore(store);
      setIsLoading(false);
    };

    resolveStore();
    
    // Listen for branding updates (Dynamic Theme Engine sync)
    const handleBrandingChange = () => {
      const activeUser = localStorage.getItem('lynqar_auth_user');
      const storeId = activeUser ? JSON.parse(activeUser).storeId : DEFAULT_STORE_ID;
      const updated = storeService.getStoreById(storeId || DEFAULT_STORE_ID);
      setCurrentStore(updated);
    };

    window.addEventListener('store-branding-update', handleBrandingChange);
    window.addEventListener('storage', handleBrandingChange); // Also sync on local storage changes
    return () => {
      window.removeEventListener('store-branding-update', handleBrandingChange);
      window.removeEventListener('storage', handleBrandingChange);
    };
  }, [storeSlug]);

  /**
   * Performance optimization: Branding variables compute karna
   * Inhe hum base styles (CSS Variables) mein inject karenge.
   */
  const brandingStyles = useMemo(() => {
    if (!currentStore?.branding) return {};
    
    const { primaryColor, accentColor, buttonRadius, fontFamily } = currentStore.branding;
    
    return {
      '--lynqar-primary': primaryColor,
      '--lynqar-accent': accentColor,
      '--lynqar-font': fontFamily === 'Serif' ? '"Inter", serif' : 
                      fontFamily === 'Mono' ? 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' : 
                      '"Inter", sans-serif',
      '--lynqar-radius': buttonRadius === 'rounded-full' ? '9999px' : 
                         buttonRadius === 'rounded-3xl' ? '1.5rem' : 
                         buttonRadius === 'rounded-xl' ? '0.75rem' : '0px'
    } as React.CSSProperties;
  }, [currentStore?.branding]);

  return {
    store: currentStore,
    isLoading,
    brandingStyles,
    isDefault: currentStore?.id === DEFAULT_STORE_ID
  };
};
